import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { appI18nNamespaces, appI18nResources } from '@/app/providers/i18nResources';

export const SUPPORTED_LANGUAGES = ['cs', 'en'] as const;
export type SupportedLng = (typeof SUPPORTED_LANGUAGES)[number];

const LANG_STORAGE_KEY = 'phx-lang';

function isSupported(value: string | null | undefined): value is SupportedLng {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value ?? '');
}

function readStoredLanguage(): SupportedLng | null {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    return isSupported(stored) ? stored : null;
  } catch {
    return null;
  }
}

function detectLanguage(): SupportedLng {
  // Explicitní URL override (?lang=cs|en) má přednost — hodí se pro sdílené odkazy a testy.
  const param = new URLSearchParams(window.location.search).get('lang');
  if (isSupported(param)) return param;
  // Jinak trvalá volba uživatele z localStorage, jinak výchozí čeština.
  return readStoredLanguage() ?? 'cs';
}

void i18n.use(initReactI18next).init({
  lng: detectLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  defaultNS: 'common',
  ns: appI18nNamespaces,
  resources: appI18nResources,
  interpolation: {
    escapeValue: false,
  },
});

/** Přepne jazyk aplikace a uloží volbu do `localStorage`, aby přežila reload. */
export function setAppLanguage(lng: SupportedLng): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lng);
  } catch {
    // localStorage nedostupné (např. privátní režim) — přepneme aspoň pro aktuální session.
  }
  void i18n.changeLanguage(lng);
}

export default i18n;
